import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Stack,
  Typography,
  alpha,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  Sparkles,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
} from 'recharts';

// ── Status Badge ──────────────────────────────────────────────
export type StatusLevel = 'success' | 'warning' | 'critical' | 'info' | 'neutral';

const STATUS_CONFIG: Record<StatusLevel, { label: string; color: string; icon: React.ReactNode }> = {
  success: { label: 'Good', color: '#22c55e', icon: <CheckCircle2 size={12} /> },
  warning: { label: 'Warning', color: '#f59e0b', icon: <AlertTriangle size={12} /> },
  critical: { label: 'Critical', color: '#ef4444', icon: <XCircle size={12} /> },
  info: { label: 'Info', color: '#3b82f6', icon: <Sparkles size={12} /> },
  neutral: { label: 'N/A', color: '#6b7280', icon: <Minus size={12} /> },
};

export function StatusBadge({ status, label, size = 'small' }: { status: StatusLevel; label?: string; size?: 'small' | 'medium' }) {
  const theme = useTheme();
  const config = STATUS_CONFIG[status];
  const color = status === 'neutral' ? theme.palette.text.secondary
              : status === 'info' ? theme.palette.info.main
              : config.color;
  const displayLabel = label || config.label;
  return (
    <Chip
      icon={<Box sx={{ display: 'flex', color: 'inherit' }}>{config.icon}</Box>}
      label={displayLabel}
      size={size}
      sx={{
        height: size === 'small' ? 22 : 28,
        fontSize: size === 'small' ? '0.68rem' : '0.78rem',
        fontWeight: 600,
        bgcolor: alpha(color, 0.1),
        color,
        border: `1px solid ${alpha(color, 0.2)}`,
        '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
      }}
    />
  );
}

export function ProjectStatusBadge({ lastScanDate }: { lastScanDate?: string | null }) {
  if (!lastScanDate) return <StatusBadge status="neutral" label="Never Scanned" />;
  const daysSince = Math.floor((Date.now() - new Date(lastScanDate).getTime()) / (1000 * 60 * 60 * 24));
  if (daysSince > 30) return <StatusBadge status="critical" label="Stale" size="medium" />;
  return <StatusBadge status="success" label="Active" size="medium" />;
}

// ── Score Widget (circular gauge) ─────────────────────────────
export function ScoreWidget({
  score,
  maxScore = 100,
  size = 120,
  label,
  sublabel,
  showRag,
  rag,
}: {
  score: number | null;
  maxScore?: number;
  size?: number;
  label?: string;
  sublabel?: string;
  showRag?: boolean;
  rag?: string | null;
}) {
  const theme = useTheme();
  if (score == null) {
    return (
      <Box sx={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Skeleton variant="circular" width={size} height={size} />
      </Box>
    );
  }

  const pct = Math.min(score / maxScore, 1);
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct);
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  const ragLabel = (rag || '').toUpperCase();

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none"
            stroke={alpha(theme.palette.divider, 0.3)}
            strokeWidth={8}
          />
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none"
            stroke={color}
            strokeWidth={8}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontSize: size * 0.28, fontWeight: 800, color, lineHeight: 1 }}>
            {Math.round(score)}
          </Typography>
          <Typography sx={{ fontSize: size * 0.1, color: 'text.disabled', fontWeight: 500 }}>
            /{maxScore}
          </Typography>
        </Box>
      </Box>
      {label && (
        <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 1, color: 'text.primary' }}>
          {label}
        </Typography>
      )}
      {sublabel && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
          {sublabel}
        </Typography>
      )}
      {showRag && ragLabel && (
        <Chip
          label={ragLabel}
          size="small"
          sx={{
            mt: 0.75, height: 20, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: alpha(color, 0.12), color,
          }}
        />
      )}
    </Box>
  );
}

// ── Mini Score Badge (inline) ─────────────────────────────────
export function MiniScoreBadge({ score, label }: { score: number | null; label?: string }) {
  if (score == null) return null;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {label && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>}
      <Chip
        label={`${Math.round(score)}/100`}
        size="small"
        sx={{
          height: 20, fontSize: '0.65rem', fontWeight: 700,
          bgcolor: alpha(color, 0.1), color,
          border: `1px solid ${alpha(color, 0.25)}`,
        }}
      />
    </Stack>
  );
}

// ── KPI Card ──────────────────────────────────────────────────
export interface KpiCardData {
  id: string;
  label: string;
  value: string | number | boolean;
  score?: number | null;
  status?: StatusLevel;
  trend?: 'up' | 'down' | 'flat';
  trendValue?: string;
  priority?: string;
  // Expanded details
  issue?: string;
  reason?: string;
  businessImpact?: string;
  affectedFiles?: string[];
  remediationPlan?: string;
  owner?: string;
  estimatedEffort?: string;
  aiRecommendation?: string;
}

export function KpiCard({ data, onClick }: { data: KpiCardData; onClick?: () => void }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const displayValue = typeof data.value === 'boolean'
    ? (data.value ? 'Pass' : 'Fail')
    : String(data.value);

  const status = data.status || (
    data.score != null
      ? data.score >= 80 ? 'success' : data.score >= 60 ? 'warning' : 'critical'
      : 'neutral'
  );
  const statusConfig = STATUS_CONFIG[status];
  const statusColor = status === 'neutral' ? theme.palette.text.secondary
                    : status === 'info' ? theme.palette.info.main
                    : statusConfig.color;
  const hasDetails = !!(data.issue || data.reason || data.remediationPlan || data.aiRecommendation);

  const handleClick = () => {
    if (hasDetails) setExpanded(!expanded);
    onClick?.();
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        p: 2, borderRadius: 2,
        border: '1px solid', borderColor: expanded ? alpha(statusColor, 0.3) : 'divider',
        bgcolor: expanded ? alpha(statusColor, 0.02) : 'background.paper',
        cursor: hasDetails ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': hasDetails ? {
          borderColor: alpha(statusColor, 0.4),
          bgcolor: alpha(statusColor, 0.03),
          transform: 'translateY(-1px)',
          boxShadow: `0 2px 8px ${alpha(statusColor, 0.08)}`,
        } : {},
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {data.label}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: statusColor, lineHeight: 1.1 }}>
              {displayValue}
            </Typography>
            {data.trend && (
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{
                color: data.trend === 'up' ? '#22c55e' : data.trend === 'down' ? '#ef4444' : 'text.disabled',
              }}>
                {data.trend === 'up' ? <TrendingUp size={12} /> : data.trend === 'down' ? <TrendingDown size={12} /> : <Minus size={12} />}
                {data.trendValue && <Typography sx={{ fontSize: 10, fontWeight: 600 }}>{data.trendValue}</Typography>}
              </Stack>
            )}
          </Stack>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5}>
          <StatusBadge status={status} />
          {data.priority && (
            <Chip
              label={data.priority}
              size="small"
              sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, textTransform: 'capitalize' }}
            />
          )}
          {hasDetails && (
            <Box sx={{ color: 'text.disabled', mt: 0.5 }}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Box>
          )}
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1.5}>
            {data.issue && (
              <DetailRow label="Issue" value={data.issue} />
            )}
            {data.reason && (
              <DetailRow label="Reason" value={data.reason} />
            )}
            {data.businessImpact && (
              <DetailRow label="Business Impact" value={data.businessImpact} />
            )}
            {data.aiRecommendation && (
              <DetailRow label="AI Recommendation" value={data.aiRecommendation} icon={<Sparkles size={12} color="#8b5cf6" />} />
            )}
            {data.remediationPlan && (
              <DetailRow label="Remediation Plan" value={data.remediationPlan} />
            )}
            {data.affectedFiles && data.affectedFiles.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                  Affected Files
                </Typography>
                <Stack spacing={0.25}>
                  {data.affectedFiles.slice(0, 5).map((f, i) => (
                    <Typography key={i} sx={{ fontSize: 11, color: 'text.primary', fontFamily: 'monospace', bgcolor: alpha(theme.palette.text.primary, 0.04), px: 1, py: 0.25, borderRadius: 0.5 }}>
                      {f}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
            {(data.owner || data.estimatedEffort) && (
              <Stack direction="row" spacing={2}>
                {data.owner && <DetailRow label="Owner" value={data.owner} inline />}
                {data.estimatedEffort && <DetailRow label="Effort" value={data.estimatedEffort} inline />}
              </Stack>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

function DetailRow({ label, value, icon, inline }: { label: string; value: string; icon?: React.ReactNode; inline?: boolean }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {icon}
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: 'text.primary', mt: 0.25, lineHeight: 1.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Score Trend Chart ─────────────────────────────────────────
export interface TrendPoint {
  date: string;
  score: number;
  label?: string;
}

export function ScoreTrendChart({
  data,
  height = 180,
  color = '#3b82f6',
  showArea = true,
  title,
}: {
  data: TrendPoint[];
  height?: number;
  color?: string;
  showArea?: boolean;
  title?: string;
}) {
  const theme = useTheme();
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>No trend data available</Typography>
      </Box>
    );
  }

  // Single data point → render as a dot on the chart
  if (data.length === 1) {
    return (
      <Box>
        {title && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
            {title}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: theme.palette.text.disabled }}
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: theme.palette.text.disabled }}
              axisLine={false}
              tickLine={false}
            />
            <ReTooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                fontSize: 12,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
              formatter={(value: any) => [`${Math.round(Number(value))}/100`, 'Score']}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke={color}
              strokeWidth={0}
              dot={{ fill: color, r: 6, strokeWidth: 2, stroke: color }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  const Chart = showArea ? AreaChart : LineChart;

  return (
    <Box>
      {title && (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <Chart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: theme.palette.text.disabled }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: theme.palette.text.disabled }}
            axisLine={false}
            tickLine={false}
          />
          <ReTooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
              fontSize: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
            formatter={(value: any) => [`${Math.round(Number(value))}/100`, 'Score']}
          />
          {showArea && (
            <defs>
              <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
          )}
          {showArea ? (
            <Area
              type="monotone"
              dataKey="score"
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${color.replace('#', '')})`}
              dot={{ fill: color, r: 3 }}
              activeDot={{ r: 5, fill: color }}
            />
          ) : (
            <Line
              type="monotone"
              dataKey="score"
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, r: 3 }}
              activeDot={{ r: 5, fill: color }}
            />
          )}
        </Chart>
      </ResponsiveContainer>
    </Box>
  );
}

// ── Tool Contributors Widget ──────────────────────────────────
export function ToolContributors({
  connected,
  notConnected,
}: {
  connected: string[];
  notConnected: string[];
}) {
  const theme = useTheme();
  const total = connected.length + notConnected.length;
  const toolLabels: Record<string, string> = {
    sonarqube: 'SonarQube',
    blackduck: 'BlackDuck',
    ghe: 'GitLeaks',
    trivy: 'Trivy',
    ai_agent: 'AI Agent',
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
        Calculated from <strong>{connected.length} / {total}</strong> AI Score Contributors
      </Typography>
      <Stack spacing={0.5}>
        {connected.map((t) => (
          <Stack key={t} direction="row" alignItems="center" spacing={0.75}>
            <CheckCircle2 size={13} color="#22c55e" />
            <Typography sx={{ fontSize: 12, color: 'text.primary' }}>
              {toolLabels[t] || t}
            </Typography>
          </Stack>
        ))}
        {notConnected.map((t) => (
          <Stack key={t} direction="row" alignItems="center" spacing={0.75}>
            <XCircle size={13} color={theme.palette.text.disabled} />
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
              {toolLabels[t] || t}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ── Section Card ──────────────────────────────────────────────
export function SectionCard({
  children,
  title,
  icon,
  action,
  noPadding,
  sx: sxOverride,
}: {
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  noPadding?: boolean;
  sx?: Record<string, any>;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        ...sxOverride,
      }}
    >
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.5, py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {icon && <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>}
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
              {title}
            </Typography>
          </Stack>
          {action}
        </Stack>
      )}
      <Box sx={noPadding ? {} : { p: 2.5 }}>
        {children}
      </Box>
    </Box>
  );
}

// ── Empty State ───────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }: {
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      py: 6, px: 3, textAlign: 'center',
    }}>
      {icon && (
        <Box sx={{
          width: 48, height: 48, borderRadius: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 2, color: 'primary.main',
        }}>
          {icon}
        </Box>
      )}
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 320, mb: action ? 2 : 0 }}>
          {description}
        </Typography>
      )}
      {action}
    </Box>
  );
}

// ── Skeleton loaders ──────────────────────────────────────────
export function KpiCardSkeleton() {
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Skeleton width="40%" height={14} sx={{ mb: 1 }} />
      <Skeleton width="30%" height={28} sx={{ mb: 0.5 }} />
      <Skeleton width="50%" height={14} />
    </Box>
  );
}

export function SectionSkeleton({ height = 200 }: { height?: number }) {
  return (
    <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height }}>
      <Skeleton width="30%" height={20} sx={{ mb: 2 }} />
      <Skeleton width="100%" height={height - 80} />
    </Box>
  );
}
